import { React, useState, useEffect, useRef } from 'react';
import { Logout } from '../auth/Auth';
import apiClient from '../auth/authenticatedClient';
import { TOAST_TIMEOUT } from '../constants';
import {
  List,
  ListItem,
  ListIcon,
  OrderedList,
  UnorderedList,
  Divider,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  IconButton,
  Text,
  useDisclosure,
  Skeleton,
  defineStyle, defineStyleConfig,
  Grid,
  useToast,
  Button, FormControl, FormLabel, Input, Stack, InputGroup, InputRightElement, Center, Flex, Heading, Box, useBreakpointValue
} from '@chakra-ui/react'
import {
  ChevronRightIcon,
  ChevronLeftIcon,
  AddIcon
} from '@chakra-ui/icons';
import ProtectedHeader from '../ProtectedHeader';
import ProtectedFooter from '../ProtectedFooter';
import { useNavigate } from 'react-router-dom';
import { errorToast } from '../error/errorToast';
import { successToast } from '../error/errorToast';
import { computeStreak } from './streaks';
import { StreakBadge } from './StreakBadge';
import { CheckBurst } from './CheckBurst';
import { celebrateCheckIn } from './rewards';


// define an extra small size style
const xs = defineStyle({
  fontSize: 'xs',
  px: '1',
  h: '8',
  borderRadius: 'sm',
})

/*
 * Habit label with a trailing chevron that always hugs the last word.
 * The last word and the icon share a no-wrap span, so when space runs out
 * they move to the next line together — the chevron can never end up on a
 * line of its own. (A U+2060 word joiner doesn't work here: browsers ignore
 * it at the text/inline-SVG boundary.)
 */
function PromptLabel({ prompt }) {
  const words = prompt.trim().split(/\s+/);
  const lastWord = words.pop();
  return (
    <Text as='span'>
      {words.length > 0 && words.join(' ') + ' '}
      <Text as='span' whiteSpace='nowrap'>
        {lastWord}
        <ChevronRightIcon />
      </Text>
    </Text>
  );
}

function Habits() {
  const formPadding = useBreakpointValue({ base: '5', md: '8' });
  const [habits, setHabits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  // Check-ins in flight, keyed by `${habitId}:${day}` — blocks double-clicks
  // on a cell while its optimistic update is being confirmed by the server.
  const pendingCells = useRef(new Set());
  // The cell the user just checked: only that one plays the full burst
  // animation, so already-checked cells stay still on page load.
  const [burstCell, setBurstCell] = useState(null);
  const [habitPrompt, setHabitPrompt] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast();
  const oneDayInMilliseconds = 24 * 60 * 60 * 1000; // 1 day in milliseconds
  const currentDate = new Date();
  const previousDate = new Date(currentDate.getTime() - oneDayInMilliseconds);
  const [displayedDates, setDisplayedDates] = useState([previousDate.getTime(), currentDate.getTime()]);
  const leftMostDateOffset = 7;
  const leftMostDate = new Date(currentDate.getTime() - leftMostDateOffset * oneDayInMilliseconds);
  const [displayDateOffset, setDisplayDateOffset] = useState(0);
  const navigate = useNavigate();
  // Date columns per viewport width. The unchecked state is a slim circle, so
  // rows can afford one more column than the pre-streak red-X layout could.
  const numDisplayedDates = useBreakpointValue({ base: 2, sm: 4, md: 5, lg: 6, xl: 7 });
  // Header and habit rows share one grid template so date labels sit exactly
  // over their cells and every streak badge lands on the same vertical line:
  // prompt (flexible) | badge slot | left arrow | date cells | right arrow.
  // Cell width must fit the widest date label ("12/31") at that font size.
  const cellWidth = useBreakpointValue({ base: '36px', md: '40px' }) ?? '36px';
  const arrowColWidth = '24px';
  const badgeColWidth = '44px';
  const gridTemplateColumns =
    `minmax(0, 1fr) ${badgeColWidth} ${arrowColWidth} repeat(${displayedDates.length}, ${cellWidth}) ${arrowColWidth}`;

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });


  const generateDates = (numDates) => {
    const dates = [];
    for (let i = 0; i < numDates; i++) {
      dates.push(new Date(currentDate.getTime() - (numDates - i - 1) * oneDayInMilliseconds).getTime());
    }
    return dates;
  }

  useEffect(() => {
    let newDates = generateDates(numDisplayedDates);
    let tempN = displayDateOffset;
    if (tempN > 0) {
      for (let i = 0; i < tempN; i++) {
        newDates = newDates.map(date => date + oneDayInMilliseconds);
      }
    } else if (tempN < 0) {
      for (let i = 0; i > tempN; i--) {
        newDates = newDates.map(date => date - oneDayInMilliseconds);
      }
    }
    setDisplayedDates(newDates);
  }, [numDisplayedDates]);

  useEffect(() => {
    fetchHabits()
      .then(data => {
        setHabits(data);
        setIsLoading(false); // Data fetched, set loading to false
      })
      .catch(error => {
        console.error(error);
        errorToast(toast, error.toString());
        setIsLoading(false); // Error occurred, set loading to false
        if (error.response?.status === 401 || error.response?.status === 403) {
          navigate('/login');
        }
      });
  }, [updateTrigger]);

  const fetchHabits = async () => {
    const response = await apiClient.get(`/habit`);
    return response.data;
  };

  const patchHabitLogs = (habitId, updateLogs) => {
    setHabits((prev) =>
      prev.map((h) => (h.id === habitId ? { ...h, habitLogs: updateLogs(h.habitLogs) } : h))
    );
  };

  // Reward feedback for a successful check-in. Fires optimistically at click
  // time (before the server confirms) so the reward lands in the same moment
  // as the action; a failed request reverts the check with an error toast.
  const handleRecordHabitClick = (habit, date) => {
    const cellKey = `${habit.id}:${new Date(date).toDateString()}`;
    if (pendingCells.current.has(cellKey)) return;
    pendingCells.current.add(cellKey);

    const existing = habit.habitLogs.find(
      (log) => new Date(log.recordTs).toDateString() === new Date(date).toDateString()
    );

    if (existing) {
      // Optimistically un-check; restore the log if the delete fails.
      patchHabitLogs(habit.id, (logs) => logs.filter((log) => log.id !== existing.id));
      apiClient.delete(`/habit/${habit.id}/entry/${existing.id}`)
        .catch((error) => {
          patchHabitLogs(habit.id, (logs) => [...logs, existing]);
          errorToast(toast, error.toString());
        })
        .finally(() => pendingCells.current.delete(cellKey));
      return;
    }

    // Optimistically check the cell so the reward is instant, then swap the
    // placeholder for the server-created log (or remove it on failure).
    const tempLog = { id: `pending-${date}`, recordTs: new Date(date).toISOString() };
    patchHabitLogs(habit.id, (logs) => [...logs, tempLog]);
    setBurstCell(cellKey);
    celebrateCheckIn({ toast, prompt: habit.prompt, logs: [...habit.habitLogs, tempLog], date });
    apiClient.post(`/habit/${habit.id}/entry`, { date })
      .then((response) => {
        patchHabitLogs(habit.id, (logs) =>
          logs.map((log) => (log.id === tempLog.id ? { ...tempLog, ...response.data } : log))
        );
      })
      .catch((error) => {
        patchHabitLogs(habit.id, (logs) => logs.filter((log) => log.id !== tempLog.id));
        errorToast(toast, error.toString());
      })
      .finally(() => pendingCells.current.delete(cellKey));
  };

  const doesHabitExistOnDate = (habitLogs, date) => {
    for (let i = 0; i < habitLogs.length; i++) {
      if (new Date(habitLogs[i].recordTs).toDateString() === new Date(date).toDateString()) {
        return true;
      }
    }
    return false;
  }

  const compareDay = (date1, date2) => {
    return Math.floor(date1 / oneDayInMilliseconds) - Math.floor(date2 / oneDayInMilliseconds);
  }

  const handleAddNewHabit = (event) => {
    event.preventDefault();
    apiClient.post(`/habit`, { prompt: habitPrompt, frequency: '0 0 * * *' })
      .then(() => {
        toast({
          title: 'Habit added',
          description: 'You have added a new habit',
          status: 'success',
          duration: TOAST_TIMEOUT,
          isClosable: true,
        });
        setUpdateTrigger(updateTrigger + 1);
        onClose();
        setHabitPrompt('');
      })
      .catch((error) => {
        let message = error.toString();
        if (error.response?.status === 401 || error.response?.status === 403) {
          navigate('/login');
        } else if (error.response?.status === 400) {
          message = 'Please enter a valid prompt';
        } else if (error.response?.status === 409) {
          message = 'You already have a habit with this prompt';
        }
        errorToast(toast, message);
      });
  }

  const moveOneDayBackward = () => {
    const newDisplayedDates = displayedDates.map(date => date - oneDayInMilliseconds);
    setDisplayedDates(newDisplayedDates);
    setDisplayDateOffset(Math.max(-leftMostDateOffset, displayDateOffset - 1));
  }

  const moveOneDayForward = () => {
    const newDisplayedDates = displayedDates.map(date => date + oneDayInMilliseconds);
    setDisplayedDates(newDisplayedDates);
    setDisplayDateOffset(Math.min(0, displayDateOffset + 1));
  }



  return (
    <Flex direction="column" h="100vh" minW="350px">
      <ProtectedHeader />
      <Center flex="1">
        <Box
          w={{ base: '100%', md: '85%', lg: '60%' }} // Responsive width
          p={formPadding} // Responsive padding
          rounded="lg" // Optionally rounds the corners
        >
          {isLoading ? (
            <Stack flex="1" align="stretch">
              <Skeleton height='20px' />
              <Skeleton height='20px' />
              <Skeleton height='20px' />
            </Stack>
          ) : (
            <List spacing={1}>
              <Stack divider={<Divider />}>
                <ListItem key='header' >
                  <Grid templateColumns={gridTemplateColumns} gap={1} alignItems='center'>
                    <Text fontWeight='bold' fontSize={{ base: 'sm', md: 'md', lg: 'lg' }} m={{ base: '1', md: '2', lg: '4' }}>Habits</Text>
                    <Box />
                    <IconButton size='xs' variant='ghost'
                      aria-label='Show earlier dates'
                      isDisabled={compareDay(displayedDates[0], leftMostDate.getTime()) <= 0}
                      onClick={moveOneDayBackward}
                      icon={<ChevronLeftIcon />}></IconButton>
                    {displayedDates.map((date) => (
                      <Text key={date} textAlign='center' fontSize={{ base: 'sm', md: 'md', lg: 'lg' }}>{formatDate(date)}</Text>
                    ))}
                    <IconButton
                      size='xs'
                      variant='ghost'
                      aria-label='Show later dates'
                      onClick={moveOneDayForward}
                      icon={<ChevronRightIcon />}
                      isDisabled={compareDay(displayedDates[displayedDates.length - 1], currentDate.getTime()) >= 0}
                    ></IconButton>
                  </Grid>
                </ListItem>
                {habits.map((habit, index) => (
                  <ListItem key={index}
                    height="auto"
                  >
                    <Grid templateColumns={gridTemplateColumns} gap={1} alignItems='center'>
                      <Button
                        colorScheme="teal"
                        variant="ghost"
                        whiteSpace="normal" height="auto" blockSize="auto"
                        wordBreak="break-word"
                        textAlign="left"
                        justifyContent="flex-start"
                        minW={0}
                        px={2}
                        py={2}
                        size={{ base: 'sm', md: 'md', lg: 'lg' }}
                        onClick={() => navigate(`/habits/${habit.id}`)}
                      >
                        <PromptLabel prompt={habit.prompt} />
                      </Button>
                      <Flex justify='flex-end' align='center'>
                        <StreakBadge streak={computeStreak(habit.habitLogs)} />
                      </Flex>
                      <Box />
                      {displayedDates.map((date) => {
                        const checked = doesHabitExistOnDate(habit.habitLogs, date);
                        return (
                          <Button
                            key={`${habit.id}-${date}`}
                            size={{ base: 'sm', md: 'md' }}
                            w='100%'
                            minW={0}
                            // Inline style: the size variant's padding is emitted
                            // after the px style prop, so px={0} loses to it.
                            style={{ paddingInline: 0 }}
                            position='relative'
                            overflow='visible'
                            aria-pressed={checked}
                            aria-label={`${checked ? 'Recorded' : 'Record'} for ${formatDate(date)}`}
                            onClick={() => handleRecordHabitClick(habit, date)}
                          >
                            {checked ? (
                              <CheckBurst burst={burstCell === `${habit.id}:${new Date(date).toDateString()}`} />
                            ) : (
                              // Unchecked is deliberately neutral (not a red X):
                              // highlighting misses demotivates more than it helps.
                              <Box
                                as='span'
                                display='inline-block'
                                boxSize='0.9em'
                                borderRadius='full'
                                border='2px solid'
                                borderColor='gray.300'
                              />
                            )}
                          </Button>
                        );
                      })}
                      <Box />
                    </Grid>
                  </ListItem>
                ))}
                <IconButton colorScheme='teal' onClick={onOpen} aria-label='Add a habit' icon={<AddIcon />} />
              </Stack>
            </List>
          )}
        </Box>
      </Center>
      <Modal onClose={onClose} size='full' isOpen={isOpen}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Habit</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form onSubmit={handleAddNewHabit}>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Prompt</FormLabel>
                  <Input
                    type="text"
                    maxLength={65}
                    errorBorderColor="red.300"
                    placeholder="Example prompt: Did you exercise today?"
                    onChange={(event) => setHabitPrompt(event.target.value)}
                    value={habitPrompt}
                  />
                </FormControl>
                <Text size='xs'>Frequency: Every day (nothing else is supported at the moment)</Text>
                <Center mt={4}>
                  <Button colorScheme='teal' type='submit'>Save</Button>
                </Center>
              </Stack>
            </form>
          </ModalBody>
          <ModalFooter>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ProtectedFooter />
    </Flex>
  );
}

export { Habits };



